import React, { Fragment, useEffect, useState } from 'react';
import { Button } from 'semantic-ui-react';
import { connect } from 'react-redux';
import SemanticUiModal from '../components/modal';
import ElementLoader from '../components/element_loader';
import useApiService from '../services/useApiService';
import { getText, pickAttributes } from '../helpers';
import { formatRiskColumns, formatLikelihood, formatConsequences } from '../helpers/risk_helpers';
import EasyTable from './easy_table';
import { getColumns } from './risk_comparison_columns';
import UnwantedEventDescription from './controls_component/unwanted_event_description';
import { snackbarAddMessage } from '../actions/snackbar';

import { store } from '../store';

import './risk_comparison_modal.css';

const ConfirmCopyUnwantedEventData = (props) => {
  const { confirmCopyUnwantedEventData, hideModal } = props;
  const modalOptions = { className: "confirm-copy-unwanted-event-data", size: "tiny" }

  return (
    <SemanticUiModal
      modalOptions={modalOptions}
      header={<div>{getText("Confirm Copy - Paste")}</div>}
      content={
        <body>
          <label>Proceeding will apply these changes:</label>

          <ul>
            <li>The controls from the selected row will be added to the existing controls in the unwanted event of the current plan.</li>
            <li>The Likelihood and Consequence information from the selected row will replace the values in the unwanted event of the current plan.</li>
          </ul>

        </body>
      }
      actions={
        <div>
          <Button positive size="medium" className="" onClick={confirmCopyUnwantedEventData}>
            {getText("Confirm Copy - Paste")}
          </Button>
        </div>
      }
      closeModal={hideModal}
    />
  );
}

const RiskComparisonModal = (props) => {
  const { risk, hideModal, translations, refreshUpdatedRisk } = props;
  const perPage = 15;
  const [risks, setRisks] = useState([]);
  const [page, setPage] = useState(1);
  const [{ isLoading, response: fetchedRisks, error: fetchRisksError }, fetchSimilarRisks] = useApiService({ url: `/risks/${risk.id}/compare`, defaultResponse: [] });

  const [ confirmationModalShown, setConfirmationModal ] = useState(false);
  const [ selectedRiskId, setSelectedRiskId ] = useState();

  useEffect(() => {
    setPage(1);
    setRisks([]);
  }, [risk]);

  useEffect(() => {
    if (!risk.id) return;
    
    fetchSimilarRisks({ params: { per_page: perPage, page } });
  }, [page, risk]);

  useEffect(() => {
    if (!fetchedRisks) return;

    if (page === 1) {
      setRisks(fetchedRisks);
    } else {
      setRisks(prevRisks => [...prevRisks, ...fetchedRisks]);
    }
  }, [fetchedRisks]);

  const setNewPage = () => {
    if (fetchedRisks && fetchedRisks.length < perPage) return;
    setPage(p => p + 1);
  };

  const onCopyUnwantedEventData = (selectedRisk) => {
    setSelectedRiskId(selectedRisk.id);
    setConfirmationModal(true)
  };

  const confirmCopyUnwantedEventData = () => {
    copyUnwantedEventData({ source_risk_id: selectedRiskId });
  }

  const onCopyUnwantedEventSuccess = (updatedRisk) => {
    store.dispatch(snackbarAddMessage({ header: getText("Success"), content: getText("Unwanted event data has been copied and pasted."), type: "success" }));
    hideModal();
    refreshUpdatedRisk(updatedRisk);
  }

  const onCopyUnwantedEventError = () => {
    store.dispatch(snackbarAddMessage({ header: getText("Error"), content: getText("Something went wrong while trying to copy and paste the unwanted event contents. Please close the modal and try again."), type: "error" }));
  }

  const [{ isLoading: isCopyingRisk }, copyUnwantedEventData] = useApiService(
    {
      url: `/risks/${risk.id}/copy_unwanted_event_data`,
      method: 'put',
      onSuccess: onCopyUnwantedEventSuccess,
      onError: onCopyUnwantedEventError
    }
  );

  const modalOptions = { className: "risk-comparison-modal fit-screen", size: "fullscreen" };

  const renderHeader = () => {
    return (
      <Fragment>
        <div>{getText("Compare Same Unwanted Events")}</div>
        <UnwantedEventDescription
          workPhase={risk.work_phase_or_ambient_exposure}
          activity={risk.activity}
          hazard={risk.hazard}
          unwantedEvent={risk.unwanted_event}
        />
      </Fragment>
    );
  };

  const renderContent = () => {
    if (isLoading && page === 1) {
      return <ElementLoader isLoading={isLoading} message={getText("Fetching similar risks...")} />;
    }

    if (fetchRisksError) {
      return <div className='error-message'>{getText("Error fetching similar risks. Please try again.")}</div>;
    }

    if (!risks || risks.length === 0) {
      return <div className='no-records'>{getText("No similar risks found.")}</div>;
    }

    const data = risks.map(similarRisk => {
        const parsedMetadata = JSON.parse(similarRisk.metadata);
        const formattedRisk = formatRiskColumns(parsedMetadata, similarRisk);

        formattedRisk["likelihood"] = formatLikelihood("likelihood", parsedMetadata);
        formattedRisk["new_likelihood"] = formatLikelihood("new_likelihood", parsedMetadata);
        formattedRisk["6x6_likelihood"] = formatLikelihood("6x6_likelihood", parsedMetadata);
        formattedRisk["6x6_new_likelihood"] = formatLikelihood("6x6_new_likelihood", parsedMetadata);

        formatConsequences(formattedRisk, parsedMetadata["consequences"]);
        formatConsequences(formattedRisk, parsedMetadata["new_consequences"]);
        formatConsequences(formattedRisk, parsedMetadata["6x6_consequences"]);
        formatConsequences(formattedRisk, parsedMetadata["6x6_new_consequences"]);

        return { ...similarRisk, ...formattedRisk };
    });

    const columnProps = pickAttributes(
      props,
      'translations',
      'languageName',
      'riskRatingDict',
      'likelihoodConversionToView',
      'TagOptions',
      'instanceConfig'
    );

    return (
      <div className="risk-comparison-table-container">
        <EasyTable
          className="risk-comparison-table"
          keyField='id'
          columns={getColumns({ ...columnProps, onCopyUnwantedEventData })}
          data={data}
          lockedColumnHeadings={true}
          showHeading={false}
          translations={translations}
          fetchMore={setNewPage}
          customLoadingMessage={
            <React.Fragment>
              {
                isLoading &&
                <div className="loading-message">
                  <i className="loading circle notched icon"></i>
                  <span>{getText("Fetching more similar risks...")}</span>
                </div>
              }
            </React.Fragment>
          }
          loading={isCopyingRisk}
          readOnly={true}
        />
        {
          confirmationModalShown &&
          <ConfirmCopyUnwantedEventData
            confirmCopyUnwantedEventData={confirmCopyUnwantedEventData}
            hideModal={()=>setConfirmationModal(false)}
          />

        }
      </div>
    );
  };

  const renderActions = () => {
    return (
      <Button fluid size='medium' className="new-plan-button" onClick={hideModal} disabled={isCopyingRisk}>
        {getText("Close")}
      </Button>
    );
  };

  return (
    <SemanticUiModal
      modalOptions={modalOptions}
      header={renderHeader()}
      content={renderContent()}
      actions={renderActions()}
      closeModal={hideModal}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    translations: state.language.language.translations,
    instanceConfig: state.instance.instanceConfig,
    languageName: state.language.language.name,
  };
};

export default connect(mapStateToProps)(RiskComparisonModal);


// WEBPACK FOOTER //
// ./src/components/risk_comparison_modal.js